import { Box, Stack } from '@mui/material';
import { ReactElement, ReactNode } from 'react';

import { PaloNotice } from './palo-notice';

export interface PaloCancellationNoticeProps<T> {
    cancellations: T[];
    getId: (item: T) => number | string;
    title: ReactNode;
    /** One line per cancellation: what it was, and for whom. */
    describe: (item: T) => ReactNode;
    /** Where the cancellation's PDF opens. */
    pdfHref: (item: T) => string;
    pdfLabel: ReactNode;
    /** Further links on a line, after the PDF. */
    actions?: (item: T) => ReactNode;
    /** A sentence under the lines, saying what to do now. */
    footer?: ReactNode;
}

/**
 * The cancellations of the last days, above an order list, in both portals
 * (#2267, #2279). A supplier must not deliver them and a member must not wait
 * for them. Without one there is no notice.
 */
export const PaloCancellationNotice = <T,>({
    cancellations,
    getId,
    title,
    describe,
    pdfHref,
    pdfLabel,
    actions,
    footer,
}: PaloCancellationNoticeProps<T>): ReactElement | null => {
    if (0 === cancellations.length) {
        return null;
    }

    return (
        <Box sx={{ mb: '10px' }}>
            <PaloNotice tone="danger" title={title}>
                <Stack sx={{ gap: '2px', mt: '2px' }}>
                    {cancellations.map(item => (
                        <span key={getId(item)}>
                            {describe(item)} ·{' '}
                            <a href={pdfHref(item)} target="_blank" rel="noopener noreferrer">
                                {pdfLabel}
                            </a>
                            {actions && <> · {actions(item)}</>}
                        </span>
                    ))}
                    {footer && <span>{footer}</span>}
                </Stack>
            </PaloNotice>
        </Box>
    );
};
